import { Link } from 'react-router';
import type { CSSProperties, ReactNode } from 'react';
import { ModuleIcon } from '@/shared/icons';
import type { ModuleSlug } from '@/styles/tokens';

interface Props {
  to: string;
  label: string;
  slug: string;
  /** Hex accent for this module (from `moduleAccent[mod]`). */
  accent: string;
  hint?: string;
  /** Module slug used to pick the inline-SVG icon. Optional for back-compat. */
  mod?: ModuleSlug;
  /**
   * Custom icon node for tiles that aren't one of the 15 canonical
   * modules (e.g. Employee Hub, which lives outside the Shell). Rendered
   * in the icon slot when `mod` isn't supplied.
   */
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * Dashboard tile — Amassa Redesign v3. 14 px symmetric corner, glassy
 * `--bg-card` over `backdrop-filter: blur(8px)`. Hover lifts and adds
 * the soft accent-tinted shadow formula from hub-modern.css §8.
 *
 * Accent flows through the `--tile-accent` custom property the
 * `.hub-tile` selector reads.
 *
 * Round-8 FRONTEND-POLISH: when `mod` is supplied, render the inline-SVG
 * `<ModuleIcon>` in the top-right corner (Lucide is banned per the BQB
 * signature, so we ship a tiny hand-drawn-feel icon system instead — see
 * `web/src/shared/icons/index.tsx`).
 */
export function Tile({ to, label, slug, accent, hint, mod, icon, children }: Props) {
  const style = { ['--tile-accent' as string]: accent } as CSSProperties;
  return (
    <Link to={to} className="hub-tile" style={style} aria-label={label}>
      {(mod || icon) && (
        <span
          className="hub-tile__icon"
          aria-hidden
          style={{ color: 'var(--accent)' }}
        >
          {mod ? <ModuleIcon mod={mod} size={22} /> : icon}
        </span>
      )}
      <span className="hub-tile__slug">{slug}</span>
      <span className="hub-tile__label">{label}</span>
      {hint && <span className="hub-tile__hint">{hint}</span>}
      {children}
    </Link>
  );
}
